import { useEffect, useMemo, useState } from "react";

import RSLogo from "../components/icons/RSLogo";
import {
  ArrowRight,
  DeviceMobile,
  LockClosed,
} from "../components/icons/svg/outline";
import {
  formatSMSPhoneNumberInput,
  getAuthBackendOrigin,
  getLoginNextUrl,
  validateSMSPhoneNumber,
} from "../utils/auth";
import { getCookie } from "../utils/dom";
import { completePhoneLogin, startPhoneLogin } from "../services/api/core";

/**
 * Renders the public top bar for unauthenticated login pages.
 *
 * @returns {JSX.Element} Public login navigation bar.
 */
const LoginHeader = () => (
  <header className="h-14 w-full bg-primary text-white shadow-lg shadow-primary-900/20">
    <div className="relative mx-auto flex h-full max-w-6xl items-center justify-between px-4 sm:px-6">
      <div aria-hidden="true" className="w-24" />
      <a
        href="/"
        className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center"
        title="Return to Homepage"
      >
        <RSLogo classes="h-8 ml-auto" variant="logo_trans_white" alt="R&S logo" />
        <p className="ml-2 mr-auto hidden text-lg font-bold sm:flex">
          R&S CONSTRUCTION
        </p>
        <p className="ml-2 mr-auto flex text-lg font-bold sm:hidden">
          R&S
        </p>
      </a>
      <div aria-hidden="true" className="w-24" />
    </div>
  </header>
);

/**
 * Renders the SMS code login card.
 *
 * @param {object} props Component props.
 * @param {string} props.phoneNumber Current phone number value.
 * @param {string} props.code Current one-time code value.
 * @param {string} props.step Current SMS login step.
 * @param {string} props.message Status message to show.
 * @param {string} props.errorMessage Error message to show.
 * @param {boolean} props.isSubmitting Whether a phone login request is running.
 * @param {Function} props.onPhoneChange Handles phone input changes.
 * @param {Function} props.onCodeChange Handles code input changes.
 * @param {Function} props.onSendCode Starts phone login.
 * @param {Function} props.onCompleteCode Completes phone login.
 * @param {Function} props.onEditPhone Returns to phone entry.
 * @param {Function} props.onUsePassword Switches to password login.
 * @returns {JSX.Element} SMS code login panel.
 */
const SMSCodeLoginCard = ({
  phoneNumber,
  code,
  step,
  message,
  errorMessage,
  isSubmitting,
  onPhoneChange,
  onCodeChange,
  onSendCode,
  onCompleteCode,
  onEditPhone,
  onUsePassword,
}) => (
  <form
    className="rounded-lg border border-white/20 bg-white/90 p-8 shadow-xl shadow-primary-900/10 backdrop-blur"
    onSubmit={step === "phone" ? onSendCode : onCompleteCode}
  >
    <div className="flex items-start gap-4">
      <div className="rounded-lg bg-secondary-100 p-3 text-secondary-700">
        <DeviceMobile className="h-7 w-7" />
      </div>
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.22em] text-secondary-700">
          SMS Code Login
        </p>
        <h2 className="mt-2 text-2xl font-bold text-gray-900">
          Text me a login code
        </h2>
        <p className="mt-3 text-sm leading-6 text-gray-600">
          Enter the phone number on your account. We will text you a short code
          to complete sign in.
        </p>
      </div>
    </div>

    {message ? (
      <div
        className="mt-6 rounded-lg border border-secondary-100 bg-secondary-50 px-4 py-3 text-sm text-secondary-800"
        aria-live="polite"
      >
        {message}
      </div>
    ) : null}

    {errorMessage ? (
      <div
        className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
        aria-live="polite"
      >
        {errorMessage}
      </div>
    ) : null}

    <div className="mt-8">
      <label
        className="text-sm font-semibold text-gray-700"
        htmlFor="id_sms_number"
      >
        US Phone Number
      </label>
      <input
        className="mt-2 caret-black block min-h-[48px] w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-base text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
        type="tel"
        name="sms_number"
        id="id_sms_number"
        placeholder="123-456-7890"
        autoComplete="tel"
        value={phoneNumber}
        onChange={onPhoneChange}
        disabled={isSubmitting || step === "code"}
      />
    </div>

    {step === "code" ? (
      <>
        <div className="mt-5">
          <label
            className="text-sm font-semibold text-gray-700"
            htmlFor="id_sms_code"
          >
            Login Code
          </label>
          <input
            className="mt-2 caret-black block min-h-[48px] w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-base text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            name="sms_code"
            id="id_sms_code"
            placeholder="Enter code"
            autoComplete="one-time-code"
            enterKeyHint="done"
            value={code}
            onChange={onCodeChange}
            disabled={isSubmitting}
          />
        </div>
        <div className="mt-4 flex justify-center">
          <button
            className="text-sm font-semibold text-secondary-700 hover:text-secondary-500"
            type="button"
            onClick={onEditPhone}
            disabled={isSubmitting}
          >
            Use a different phone number
          </button>
        </div>
      </>
    ) : null}

    <button
      className={`mt-8 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-base font-semibold text-white shadow-lg shadow-primary-900/20 transition hover:bg-primary-300 focus:outline-none focus:ring-4 focus:ring-primary-100 ${isSubmitting ? "opacity-60" : ""}`}
      type="submit"
      disabled={isSubmitting}
    >
      {isSubmitting
        ? "Processing..."
        : step === "phone"
          ? "Send Login Code"
          : "Log In"}
      <ArrowRight className="h-5 w-5" />
    </button>

    <div className="mt-6 flex justify-center">
      <button
        className="text-sm font-semibold text-secondary-700 hover:text-secondary-500"
        type="button"
        onClick={onUsePassword}
      >
        Or log in via password
      </button>
    </div>
  </form>
);

/**
 * Renders the React-hosted login page while preserving allauth password login.
 *
 * @param {object} props Optional login page overrides.
 * @param {string} props.csrfToken CSRF token for the login form POST.
 * @param {string} props.errorMessage Password login error message.
 * @param {string} props.loginValue Previously submitted login value.
 * @param {string} props.passwordResetUrl URL for allauth password reset.
 * @param {string} props.redirectFieldName Allauth redirect field name, usually `next`.
 * @param {string} props.redirectFieldValue Destination to return to after login.
 * @param {string} props.formAction URL the password form posts to.
 * @returns {JSX.Element} Login page component.
 */
const Login = ({
  csrfToken = "",
  errorMessage = "",
  loginValue = "",
  passwordResetUrl = "/accounts/password/reset/",
  redirectFieldName = "next",
  redirectFieldValue = "",
  formAction = "/accounts/login/",
}) => {
  const [csrfValue, setCsrfValue] = useState(csrfToken);
  const queryParams = useMemo(
    () => new URLSearchParams(window.location.search),
    []
  );
  const backendOrigin = getAuthBackendOrigin();
  const queryError = queryParams.get("error");
  const [loginMethod, setLoginMethod] = useState(
    queryError || queryParams.get("login") ? "password" : "sms"
  );
  const [smsPhoneNumber, setSMSPhoneNumber] = useState("");
  const [smsCode, setSMSCode] = useState("");
  const [smsStep, setSMSStep] = useState("phone");
  const [smsMessage, setSMSMessage] = useState("");
  const [smsError, setSMSError] = useState("");
  const [isSMSSubmitting, setIsSMSSubmitting] = useState(false);
  const resolvedErrorMessage =
    errorMessage ||
    (queryError === "invalid"
      ? "The email or password you entered was not recognized."
      : "") ||
    (queryError === "inactive"
      ? "This account has been marked inactive. Please contact the office."
      : "");
  const resolvedLoginValue = loginValue || queryParams.get("login") || "";
  const resolvedRedirectFieldValue =
    redirectFieldValue || getLoginNextUrl(queryParams.get("next"));
  const resolvedFormAction =
    formAction === "/accounts/login/"
      ? `${backendOrigin}/accounts/login/`
      : formAction;

  useEffect(() => {
    if (csrfValue) {
      return;
    }

    fetch(`${backendOrigin}/api/core/csrf`, {
      method: "GET",
      credentials: "include",
    })
      .then((response) => response.json())
      .then((data) => setCsrfValue(data.csrfToken || getCookie("csrftoken")))
      .catch(() => setCsrfValue(getCookie("csrftoken")));
  }, [backendOrigin, csrfValue]);

  useEffect(() => {
    if (
      loginMethod !== "sms" ||
      smsStep !== "code" ||
      !("OTPCredential" in window) ||
      !navigator.credentials
    ) {
      return;
    }

    const abortController = new AbortController();
    navigator.credentials
      .get({
        otp: { transport: ["sms"] },
        signal: abortController.signal,
      })
      .then((credential) => {
        if (credential?.code) {
          setSMSCode(credential.code);
        }
      })
      .catch(() => {});

    return () => abortController.abort();
  }, [loginMethod, smsStep]);

  /**
   * Applies the shared SMS phone formatter as the user types.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event Phone input change event.
   * @returns {void}
   */
  const handleSMSPhoneChange = (event) => {
    const formattedPhoneNumber = formatSMSPhoneNumberInput(
      event.target.value,
      smsPhoneNumber
    );

    setSMSPhoneNumber(formattedPhoneNumber);
    if (validateSMSPhoneNumber(formattedPhoneNumber)) {
      setSMSError("");
    }
  };

  /**
   * Stores the current one-time SMS code value.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event Code input change event.
   * @returns {void}
   */
  const handleSMSCodeChange = (event) => {
    setSMSCode(event.target.value);
  };

  /**
   * Requests a Twilio Verify code for the submitted phone number.
   *
   * @param {React.FormEvent<HTMLFormElement>} event Phone login form submit event.
   * @returns {Promise<void>}
   */
  const handleSendSMSCode = async (event) => {
    event.preventDefault();

    if (!validateSMSPhoneNumber(smsPhoneNumber)) {
      setSMSError("Number format should be 123-456-7890");
      return;
    }

    setIsSMSSubmitting(true);
    setSMSError("");
    setSMSMessage("");

    try {
      const response = await startPhoneLogin(smsPhoneNumber);
      setSMSMessage(
        response.message || "If this number is registered, we sent a login code."
      );
      setSMSStep("code");
    } catch (error) {
      setSMSError(
        error.response?.data?.message || "Unable to send a login code right now."
      );
    } finally {
      setIsSMSSubmitting(false);
    }
  };

  /**
   * Verifies the submitted SMS code and redirects after session login.
   *
   * @param {React.FormEvent<HTMLFormElement>} event Phone login form submit event.
   * @returns {Promise<void>}
   */
  const handleCompleteSMSLogin = async (event) => {
    event.preventDefault();

    if (!smsCode.trim()) {
      setSMSError("Enter the login code sent to your phone.");
      return;
    }

    setIsSMSSubmitting(true);
    setSMSError("");

    try {
      const response = await completePhoneLogin(
        smsPhoneNumber,
        smsCode.trim(),
        resolvedRedirectFieldValue
      );
      window.location.href = response.redirect || "/app/home";
    } catch (error) {
      setSMSError(
        error.response?.data?.message || "The code was invalid or expired."
      );
    } finally {
      setIsSMSSubmitting(false);
    }
  };

  /**
   * Returns the SMS login card to the phone number entry step.
   *
   * @returns {void}
   */
  const handleEditSMSPhone = () => {
    setSMSStep("phone");
    setSMSCode("");
    setSMSMessage("");
    setSMSError("");
  };

  return (
    <div className="min-h-screen bg-custom-rsbg font-inter text-gray-900">
      <LoginHeader />

      <main className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(22,163,74,0.1),_transparent_36%),radial-gradient(circle_at_bottom_right,_rgba(10,42,13,0.1),_transparent_34%)]" />

        <div className="mx-auto flex min-h-[calc(100vh-7rem)] max-w-2xl flex-col justify-center gap-6 px-4 py-4 sm:px-6">
          <section>
            <div className="text-center">
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-gray-950 sm:text-5xl">
                Sign in to your account
              </h1>
            </div>
          </section>

          {loginMethod === "sms" ? (
            <SMSCodeLoginCard
              phoneNumber={smsPhoneNumber}
              code={smsCode}
              step={smsStep}
              message={smsMessage}
              errorMessage={smsError}
              isSubmitting={isSMSSubmitting}
              onPhoneChange={handleSMSPhoneChange}
              onCodeChange={handleSMSCodeChange}
              onSendCode={handleSendSMSCode}
              onCompleteCode={handleCompleteSMSLogin}
              onEditPhone={handleEditSMSPhone}
              onUsePassword={() => setLoginMethod("password")}
            />
          ) : (
            <form
              className="rounded-lg border border-white/70 bg-white p-4 shadow-2xl shadow-primary-900/15 sm:p-8"
              method="post"
              action={resolvedFormAction}
            >
              <input type="hidden" name="csrfmiddlewaretoken" value={csrfValue} />
              {resolvedRedirectFieldValue ? (
                <input
                  type="hidden"
                  name={redirectFieldName}
                  value={resolvedRedirectFieldValue}
                />
              ) : null}

              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-primary-50 p-3 text-primary">
                  <LockClosed className="h-7 w-7" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-950">
                    Password Login
                  </h2>
                  <p className="text-sm text-gray-500">
                    Use your company assigned email and personal password to log in.
                  </p>
                </div>
              </div>

              {resolvedErrorMessage ? (
                <div
                  className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                  aria-live="polite"
                >
                  {resolvedErrorMessage}
                </div>
              ) : null}

              <div className="mt-8">
                <label
                  className="text-sm font-semibold text-gray-700"
                  htmlFor="id_login"
                >
                  Email
                </label>
                <input
                  className="mt-2 block min-h-[48px] w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-base text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
                  type="email"
                  name="login"
                  id="id_login"
                  placeholder="Your Email"
                  autoComplete="username"
                  defaultValue={resolvedLoginValue}
                />
              </div>

              <div className="mt-5">
                <label
                  className="text-sm font-semibold text-gray-700"
                  htmlFor="id_password"
                >
                  Password
                </label>
                <input
                  className="mt-2 block min-h-[48px] w-full rounded-lg border border-gray-200 bg-gray-50 px-4 text-base text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
                  type="password"
                  name="password"
                  id="id_password"
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </div>

              <button
                className="mt-8 flex min-h-[48px] w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 text-base font-semibold text-white shadow-lg shadow-primary-900/20 transition hover:bg-primary-300 focus:outline-none focus:ring-4 focus:ring-primary-100"
                type="submit"
                id="login"
              >
                Log In
                <ArrowRight className="h-5 w-5" />
              </button>

              <div className="mt-6 flex flex-col gap-3 text-center text-sm sm:flex-row sm:items-center sm:justify-between sm:text-left">
                {/* <a href={passwordResetUrl} className="font-semibold text-secondary-700 hover:text-secondary-500 w-full text-center">
                  Forgot Password?
                </a> */}
                <p className="hover:text-secondary-500 w-full text-center">
                  To reset your password, please contact the office.
                </p>
              </div>
              <div className="mt-4 flex justify-center">
                <button
                  className="text-sm font-semibold text-secondary-700 hover:text-secondary-500"
                  type="button"
                  onClick={() => setLoginMethod("sms")}
                >
                  Or log in via phone code
                </button>
              </div>
            </form>
          )}
        </div>
      </main>

      <footer className="flex h-14 items-center justify-between px-4 text-xs text-gray-500 sm:px-6">
        <span>R&S Construction</span>
        <span>Operations App</span>
      </footer>
    </div>
  );
};

export default Login;
